import { useAuthStore } from '@/stores/auth'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { ref, toRaw, watch } from 'vue'

const localforage = localforageLib.createInstance({
	name: 'settings',
})

// Type for user settings
type UserSettings = {
	inflationRate?: number
	inflationYearIncrements?: number
}

export const useUserSettingsStore = defineStore('userSettings', () => {
	const authStore = useAuthStore()
	const values = ref<UserSettings>({})

	// Figure out if the user is viewing as another user or not and get the resulting id
	const getUserId = () => {
		return authStore.user?.salesContact?.id
	}

	// Get settings for a specific user
	const getUserSettings = async (
		userId: string
	): Promise<UserSettings | null> => {
		try {
			return await localforage.getItem<UserSettings>(userId)
		} catch (e) {
			console.error('UserSettingsStore failed to load settings:', e)
			return null
		}
	}

	// Save settings for a specific user
	const saveUserSettings = async (userId: string, settings: UserSettings) => {
		try {
			await localforage.setItem(userId, settings)
		} catch (e) {
			console.error('UserSettingsStore failed to save settings:', e)
		}
	}

	// Watch for changes to values and save them
	watch(
		values,
		async (newValues) => {
			const userId = getUserId()
			if (!userId) return

			// Only save if at least one value is defined
			if (Object.values(newValues).some((val) => val !== undefined)) {
				await saveUserSettings(userId, toRaw(newValues))
			}
		},
		{ deep: true }
	)

	// Load settings for the current user
	async function loadSettings() {
		reset()

		const userId = getUserId()
		if (!userId) return

		const settings = await getUserSettings(userId)
		if (settings) {
			values.value = settings
		}
	}

	// Clear all settings before switching users so that we don't carry over the previous user's settings before the current ones are loaded
	function reset() {
		values.value = {}
	}

	return {
		values,
		loadSettings,
		reset,
	}
})
